import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type DeliveryRaw = {
  id?: number;
  driver_id?: number | null;
  seller_id?: number | null;
  store_id?: number | null;
  account_id?: number | null;
  id_venta_ml?: string | null;
  usuario_ml_id?: number | null;
  method?: string | null;
  razon_social?: string | null;
  nombre_fantasia?: string | null;
  nombre_destinatario?: string | null;
  telefono_destinatario?: string | null;
  email_destinatario?: string | null;
  comentario_destino?: string | null;
  direccion?: string | null;
  codigo_postal?: string | null;
  localidad?: string | null;
  provincia?: string | null;
  origen?: string | null;
  url_tracking?: string | null;
  delivery_latitude?: number | null;
  delivery_longitude?: number | null;
  costo?: number | string | null;
  aditional_notes?: string | null;
  pickup_latitude?: number | null;
  pickup_longitude?: number | null;
  tracking?: number | string | null;
  cadete?: string | null;
  tipo_envio?: string | null;
  is_turbo?: boolean | null;

  status?: string | null;
  status_ml?: string | null;
  updated_by?: string | null;
  assigned_at?: string | null;
  pickup_at?: string | null;
  fecha_wynflex?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  active?: boolean | null;

  costo_envio?: number | string | null;
  total?: number | string | null;
  precio_cliente?: number | string | null;
  precio_chofer?: number | string | null;
  porcentaje_chofer?: number | string | null;
  neto_chofer?: number | string | null;
  ganancia?: number | string | null;
  descuento?: number | string | null;
  ganancia_real?: number | string | null;
};

type DeliveryStatusRaw = {
  id?: number;
  delivery_id?: number;
  status?: string | null;
  status_ml?: string | null;
  updated_by?: string | null;
  assigned_at?: string | null;
  pickup_at?: string | null;
  fecha_wynflex?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
  active?: boolean | null;
};

type DeliveryFinancialRaw = {
  id?: number;
  delivery_id?: number;
  costo_envio?: number | string | null;
  total?: number | string | null;
  precio_cliente?: number | string | null;
  precio_chofer?: number | string | null;
  porcentaje_chofer?: number | string | null;
  neto_chofer?: number | string | null;
  ganancia?: number | string | null;
  descuento?: number | string | null;
  ganancia_real?: number | string | null;
};

type DeliveriesResponse = {
  ok?: boolean;
  error?: string;
  delivery?: DeliveryRaw[] | DeliveryRaw;
  deliveries?: DeliveryRaw[] | DeliveryRaw;
  status?: DeliveryStatusRaw[] | DeliveryStatusRaw;
  financials?: DeliveryFinancialRaw[] | DeliveryFinancialRaw;
  data?: DeliveryRaw[] | DeliveryRaw;
  result?: DeliveryRaw[] | DeliveryRaw;
};

type StoreRaw = {
  id?: number | string;
  nombre_fantasia?: string;
  nombreFantasia?: string;
  name?: string;
};

type StoresResponse = {
  ok?: boolean;
  error?: string;
  stores?: StoreRaw[];
  data?: StoreRaw[];
  result?: StoreRaw[];
};

type DeliveryView = {
  id: number;
  seller_id: number | null;
  store_id: number | null;
  account_id: number | null;
  store_name: string;
  id_venta_ml: string;
  tracking: string;
  origen: string;
  nombre_destinatario: string;
  direccion: string;
  codigo_postal: string;
  localidad: string;
  provincia: string;
  zona_entrega: string;
  tipo_envio: string;
  is_turbo: boolean;
  chofer: string;
  status: string;
  status_updated_by: string;
  fecha_wynflex: string;
  status_created_at: string;
  updated_at: string;
  financial_total: number | null;
  precio_cliente: number | null;
  precio_chofer: number | null;
  telefono_destinatario: string;
  email_destinatario: string;
  comentario_destino: string;
};

type Filters = {
  estado: string;
  origen: string;
  tipoEnvio: string;
  turbo: string;
  tracking: string;
  storeName: string;
  destinatario: string;
  localidad: string;
  codigoPostal: string;
  chofer: string;
  search: string;
};

const initialFilters: Filters = {
  estado: "Todos",
  origen: "Todos",
  tipoEnvio: "Todos",
  turbo: "Todos",
  tracking: "",
  storeName: "",
  destinatario: "",
  localidad: "",
  codigoPostal: "",
  chofer: "",
  search: "",
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "30px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: "1240px",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.03)",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const mutedTextStyle: React.CSSProperties = {
  color: "var(--text-secondary)",
  fontSize: "12px",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
};

const labelTextStyle: React.CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  marginBottom: "8px",
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const selectBaseStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "46px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  outline: "none",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const emptyStateStyle: React.CSSProperties = {
  padding: "32px 20px",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  background: "rgba(0,0,0,0.65)",
};

const modalCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "980px",
  maxHeight: "85vh",
  overflow: "hidden",
  padding: 0,
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  borderBottom: "1px solid var(--border)",
  padding: "18px 20px",
};

const modalBodyStyle: React.CSSProperties = {
  maxHeight: "72vh",
  overflowY: "auto",
  padding: "20px",
};

function badgeStyle(
  tone: "neutral" | "primary" | "success" | "warning" | "danger"
): React.CSSProperties {
  const palette = {
    neutral: {
      background: "rgba(255,255,255,0.06)",
      color: "var(--text-primary)",
      border: "1px solid var(--border)",
    },
    primary: {
      background: "var(--primary-soft)",
      color: "var(--text-primary)",
      border: "1px solid var(--border)",
    },
    success: {
      background: "rgba(107,162,125,0.14)",
      color: "var(--success)",
      border: "1px solid rgba(107,162,125,0.24)",
    },
    warning: {
      background: "rgba(200,144,52,0.14)",
      color: "var(--warning)",
      border: "1px solid rgba(200,144,52,0.24)",
    },
    danger: {
      background: "rgba(185,100,95,0.14)",
      color: "var(--danger)",
      border: "1px solid rgba(185,100,95,0.24)",
    },
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "30px",
    padding: "0 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    ...palette[tone],
  };
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
}) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={selectBaseStyle}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function DetailsModal({
  row,
  onClose,
}: {
  row: DeliveryView | null;
  onClose: () => void;
}) {
  if (!row) return null;

  const detailGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px",
  };

  const detailCardStyle: React.CSSProperties = {
    border: "1px solid var(--border)",
    borderRadius: "16px",
    background: "rgba(255,255,255,0.03)",
    padding: "14px",
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={{ position: "absolute", inset: 0 }} onClick={onClose} />
      <div style={{ position: "relative", width: "100%", maxWidth: "980px" }}>
        <Card style={modalCardStyle}>
          <div style={modalHeaderStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: 800,
                  color: "var(--text-primary)",
                }}
              >
                {row.store_name}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Detalle del envío #{row.id}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={modalBodyStyle}>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "18px",
              }}
            >
              <span style={badgeStyle(getStatusTone(row.status))}>{row.status}</span>
              <span style={badgeStyle("neutral")}>{row.tipo_envio || "—"}</span>
              <span style={badgeStyle(row.is_turbo ? "warning" : "neutral")}>
                {row.is_turbo ? "Turbo" : "No turbo"}
              </span>
              <span style={badgeStyle("primary")}>
                Chofer: {row.chofer || "Sin asignar"}
              </span>
            </div>

            <div style={detailGridStyle} className="delivery-detail-grid">
              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Tracking</div>
                <div>{row.tracking || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>ID venta ML</div>
                <div>{row.id_venta_ml || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Comprador</div>
                <div>{row.nombre_destinatario || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Chofer asignado</div>
                <div>{row.chofer || "—"}</div>
              </div>

              <div style={{ ...detailCardStyle, gridColumn: "1 / -1" }}>
                <div style={labelTextStyle}>Dirección</div>
                <div>{row.direccion || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Código postal</div>
                <div>{row.codigo_postal || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Localidad</div>
                <div>{row.localidad || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Provincia</div>
                <div>{row.provincia || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Zona de entrega</div>
                <div>{row.zona_entrega || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Origen</div>
                <div>{row.origen || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Store ID</div>
                <div>{row.store_id ?? "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Seller ID</div>
                <div>{row.seller_id ?? "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Account ID</div>
                <div>{row.account_id ?? "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Fecha ML</div>
                <div>{formatDateTime(row.fecha_wynflex)}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Actualizado por</div>
                <div>{row.status_updated_by || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Email destinatario</div>
                <div>{row.email_destinatario || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Teléfono destinatario</div>
                <div>{row.telefono_destinatario || "—"}</div>
              </div>

              <div style={{ ...detailCardStyle, gridColumn: "1 / -1" }}>
                <div style={labelTextStyle}>Comentario destino</div>
                <div>{row.comentario_destino || "—"}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Precio cliente</div>
                <div>{formatCurrency(row.precio_cliente)}</div>
              </div>

              <div style={detailCardStyle}>
                <div style={labelTextStyle}>Precio chofer</div>
                <div>{formatCurrency(row.precio_chofer)}</div>
              </div>
            </div>

            <div style={{ marginTop: "20px", textAlign: "right" }}>
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <style>
        {`
          @media (max-width: 720px) {
            .delivery-detail-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}

function toArray<T>(value: T[] | T | undefined | null) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
}

function toStoreArray(payload: StoresResponse): StoreRaw[] {
  return payload.stores || payload.data || payload.result || [];
}

function normalizeStoreName(store: StoreRaw) {
  return store?.nombre_fantasia || store?.nombreFantasia || store?.name || "—";
}

function safeString(value: unknown) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function safeLower(value: unknown) {
  return safeString(value).toLowerCase();
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatCurrency(value: number | null) {
  if (value === null) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value);
}

function getStatusTone(
  status: string
): "neutral" | "primary" | "success" | "warning" | "danger" {
  const normalized = safeLower(status);

  if (
    normalized.includes("delivered") ||
    normalized.includes("entregado") ||
    normalized.includes("completed")
  ) {
    return "success";
  }

  if (
    normalized.includes("cancel") ||
    normalized.includes("returned") ||
    normalized.includes("fallido") ||
    normalized.includes("not_delivered")
  ) {
    return "danger";
  }

  if (
    normalized.includes("ready") ||
    normalized.includes("pickup") ||
    normalized.includes("route") ||
    normalized.includes("camino") ||
    normalized.includes("handling") ||
    normalized.includes("assigned")
  ) {
    return "warning";
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("created") ||
    normalized.includes("ready_to_ship") ||
    normalized.includes("to_be_agreed")
  ) {
    return "primary";
  }

  return "neutral";
}

function getLatestStatus(statuses: DeliveryStatusRaw[]) {
  if (!statuses.length) return null;

  const sorted = [...statuses].sort((a, b) => {
    const aTime = new Date(
      a.fecha_wynflex ||
        a.updated_at ||
        a.created_at ||
        a.assigned_at ||
        a.pickup_at ||
        a.completed_at ||
        0
    ).getTime();

    const bTime = new Date(
      b.fecha_wynflex ||
        b.updated_at ||
        b.created_at ||
        b.assigned_at ||
        b.pickup_at ||
        b.completed_at ||
        0
    ).getTime();

    return bTime - aTime;
  });

  return sorted[0] || null;
}

function buildCsv(rows: DeliveryView[]) {
  const headers = [
    "id",
    "tracking",
    "store_name",
    "nombre_destinatario",
    "direccion",
    "codigo_postal",
    "localidad",
    "zona_entrega",
    "chofer",
    "status",
    "id_venta_ml",
    "origen",
    "tipo_envio",
    "is_turbo",
    "seller_id",
    "store_id",
    "account_id",
  ];

  const content = [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const raw = row[header as keyof DeliveryView];
          const value = raw === null || raw === undefined ? "" : String(raw);
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(",")
    ),
  ].join("\n");

  return "\ufeff" + content;
}

function extractDeliveryRows(payload: DeliveriesResponse): DeliveryRaw[] {
  if (Array.isArray(payload.deliveries)) return payload.deliveries;
  if (Array.isArray(payload.delivery)) return payload.delivery;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.result)) return payload.result;
  if (payload.deliveries && typeof payload.deliveries === "object") return [payload.deliveries];
  if (payload.delivery && typeof payload.delivery === "object") return [payload.delivery];
  return [];
}

export default function Deliveries() {
  const { token } = useAuth();

  const [deliveries, setDeliveries] = useState<DeliveryView[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<DeliveryView | null>(null);

  const [draftFilters, setDraftFilters] = useState<Filters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [deliveriesRes, storesRes] = await Promise.all([
          authFetch("/v2/deliveries", { token }),
          authFetch("/v2/stores", { token }),
        ]);

        const deliveriesJson: DeliveriesResponse = await deliveriesRes.json().catch(() => ({}));
        const storesJson: StoresResponse = await storesRes.json().catch(() => ({}));

        if (!deliveriesRes.ok || deliveriesJson?.ok === false) {
          throw new Error(deliveriesJson?.error || "No se pudieron cargar los envíos");
        }

        if (!storesRes.ok || storesJson?.ok === false) {
          throw new Error(storesJson?.error || "No se pudieron cargar las stores");
        }

        const storesMap = new Map<string, string>();
        toStoreArray(storesJson).forEach((store) => {
          if (store?.id !== null && store?.id !== undefined) {
            storesMap.set(String(store.id), normalizeStoreName(store));
          }
        });

        const deliveryRows = extractDeliveryRows(deliveriesJson);
        const statusRows = toArray(deliveriesJson.status);
        const financialRows = toArray(deliveriesJson.financials);

const backendIsFlattened =
  deliveryRows.length > 0 &&
  deliveryRows.some(
    (row) =>
      row.status !== undefined ||
      row.status_ml !== undefined ||
      row.precio_cliente !== undefined ||
      row.precio_chofer !== undefined ||
      row.total !== undefined
  );

        let merged: DeliveryView[] = [];

        if (backendIsFlattened) {
          merged = deliveryRows
            .filter((delivery) => Number.isFinite(Number(delivery.id)))
            .map((delivery) => {
              const id = Number(delivery.id);

              const resolvedStoreName =
                safeString(delivery.nombre_fantasia) ||
                storesMap.get(String(delivery.store_id ?? "")) ||
                "—";

              return {
                id,
                seller_id:
                  delivery.seller_id !== null && delivery.seller_id !== undefined
                    ? Number(delivery.seller_id)
                    : null,
                store_id:
                  delivery.store_id !== null && delivery.store_id !== undefined
                    ? Number(delivery.store_id)
                    : null,
                account_id:
                  delivery.account_id !== null && delivery.account_id !== undefined
                    ? Number(delivery.account_id)
                    : null,
                store_name: resolvedStoreName,
                id_venta_ml: safeString(delivery.id_venta_ml) || "—",
                tracking: safeString(delivery.tracking) || "—",
                origen: safeString(delivery.origen) || "—",
                nombre_destinatario: safeString(delivery.nombre_destinatario) || "—",
                direccion: safeString(delivery.direccion) || "—",
                codigo_postal: safeString(delivery.codigo_postal) || "—",
                localidad: safeString(delivery.localidad) || "—",
                provincia: safeString(delivery.provincia) || "—",
                zona_entrega: "",
                tipo_envio: safeString(delivery.tipo_envio) || "—",
                is_turbo: Boolean(delivery.is_turbo),
                chofer: safeString(delivery.cadete) || "—",
status: safeString(delivery.status_ml) || safeString(delivery.status) || "Sin estado",
                status_updated_by: safeString(delivery.updated_by) || "—",
                fecha_wynflex: safeString(delivery.fecha_wynflex),
                status_created_at: safeString(delivery.created_at),
                updated_at: safeString(delivery.updated_at),
                financial_total: toNumber(delivery.total),
                precio_cliente: toNumber(delivery.precio_cliente),
                precio_chofer: toNumber(delivery.precio_chofer),
                telefono_destinatario: safeString(delivery.telefono_destinatario),
                email_destinatario: safeString(delivery.email_destinatario),
                comentario_destino: safeString(delivery.comentario_destino),
              };
            });
        } else {
          const statusesByDeliveryId = new Map<number, DeliveryStatusRaw[]>();
          statusRows.forEach((status) => {
            const deliveryId = Number(status.delivery_id);
            if (!Number.isFinite(deliveryId)) return;
            const list = statusesByDeliveryId.get(deliveryId) || [];
            list.push(status);
            statusesByDeliveryId.set(deliveryId, list);
          });

          const financialByDeliveryId = new Map<number, DeliveryFinancialRaw>();
          financialRows.forEach((financial) => {
            const deliveryId = Number(financial.delivery_id);
            if (!Number.isFinite(deliveryId)) return;
            financialByDeliveryId.set(deliveryId, financial);
          });

          merged = deliveryRows
            .filter((delivery) => Number.isFinite(Number(delivery.id)))
            .map((delivery) => {
              const id = Number(delivery.id);
              const latestStatus = getLatestStatus(statusesByDeliveryId.get(id) || []);
              const financial = financialByDeliveryId.get(id);

              const resolvedStoreName =
                safeString(delivery.nombre_fantasia) ||
                storesMap.get(String(delivery.store_id ?? "")) ||
                "—";

              return {
                id,
                seller_id:
                  delivery.seller_id !== null && delivery.seller_id !== undefined
                    ? Number(delivery.seller_id)
                    : null,
                store_id:
                  delivery.store_id !== null && delivery.store_id !== undefined
                    ? Number(delivery.store_id)
                    : null,
                account_id:
                  delivery.account_id !== null && delivery.account_id !== undefined
                    ? Number(delivery.account_id)
                    : null,
                store_name: resolvedStoreName,
                id_venta_ml: safeString(delivery.id_venta_ml) || "—",
                tracking: safeString(delivery.tracking) || "—",
                origen: safeString(delivery.origen) || "—",
                nombre_destinatario: safeString(delivery.nombre_destinatario) || "—",
                direccion: safeString(delivery.direccion) || "—",
                codigo_postal: safeString(delivery.codigo_postal) || "—",
                localidad: safeString(delivery.localidad) || "—",
                provincia: safeString(delivery.provincia) || "—",
                zona_entrega: "",
                tipo_envio: safeString(delivery.tipo_envio) || "—",
                is_turbo: Boolean(delivery.is_turbo),
                chofer: safeString(delivery.cadete) || "—",
                status: safeString(latestStatus?.status_ml) || safeString(latestStatus?.status) || "Sin estado",
                status_updated_by: safeString(latestStatus?.updated_by) || "—",
                fecha_wynflex: safeString(latestStatus?.fecha_wynflex),
                status_created_at: safeString(latestStatus?.created_at),
                updated_at: safeString(latestStatus?.updated_at),
                financial_total: toNumber(financial?.total),
                precio_cliente: toNumber(financial?.precio_cliente),
                precio_chofer: toNumber(financial?.precio_chofer),
                telefono_destinatario: safeString(delivery.telefono_destinatario),
                email_destinatario: safeString(delivery.email_destinatario),
                comentario_destino: safeString(delivery.comentario_destino),
              };
            });
        }

        setDeliveries(merged);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error cargando envíos");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token]);

  const estadoOptions = useMemo(() => {
    const values = Array.from(new Set(deliveries.map((item) => item.status).filter(Boolean))).sort();
    return ["Todos", ...values];
  }, [deliveries]);

  const origenOptions = useMemo(() => {
    const values = Array.from(new Set(deliveries.map((item) => item.origen).filter(Boolean))).sort();
    return ["Todos", ...values];
  }, [deliveries]);

  const tipoEnvioOptions = useMemo(() => {
    const values = Array.from(new Set(deliveries.map((item) => item.tipo_envio).filter(Boolean))).sort();
    return ["Todos", ...values];
  }, [deliveries]);

  const filteredRows = useMemo(() => {
    return deliveries.filter((row) => {
      if (appliedFilters.estado !== "Todos" && row.status !== appliedFilters.estado) {
        return false;
      }

      if (appliedFilters.origen !== "Todos" && row.origen !== appliedFilters.origen) {
        return false;
      }

      if (appliedFilters.tipoEnvio !== "Todos" && row.tipo_envio !== appliedFilters.tipoEnvio) {
        return false;
      }

      if (appliedFilters.turbo === "Sí" && !row.is_turbo) return false;
      if (appliedFilters.turbo === "No" && row.is_turbo) return false;

      if (
        appliedFilters.tracking &&
        !safeLower(row.tracking).includes(safeLower(appliedFilters.tracking))
      ) {
        return false;
      }

      if (
        appliedFilters.storeName &&
        !safeLower(row.store_name).includes(safeLower(appliedFilters.storeName))
      ) {
        return false;
      }

      if (
        appliedFilters.destinatario &&
        !safeLower(row.nombre_destinatario).includes(safeLower(appliedFilters.destinatario))
      ) {
        return false;
      }

      if (
        appliedFilters.localidad &&
        !safeLower(row.localidad).includes(safeLower(appliedFilters.localidad))
      ) {
        return false;
      }

      if (
        appliedFilters.codigoPostal &&
        !safeLower(row.codigo_postal).includes(safeLower(appliedFilters.codigoPostal))
      ) {
        return false;
      }

      if (
        appliedFilters.chofer &&
        !safeLower(row.chofer).includes(safeLower(appliedFilters.chofer))
      ) {
        return false;
      }

      if (appliedFilters.search) {
        const haystack = [
          row.tracking,
          row.store_name,
          row.nombre_destinatario,
          row.direccion,
          row.codigo_postal,
          row.localidad,
          row.chofer,
          row.status,
        ]
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(appliedFilters.search.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [deliveries, appliedFilters]);

  const totalRows = deliveries.length;
  const visibleRows = filteredRows.length;
  const entregadosRows = filteredRows.filter((item) =>
    safeLower(item.status).includes("delivered") ||
    safeLower(item.status).includes("entregado")
  ).length;
  const pendientesRows = filteredRows.filter((item) =>
    !safeLower(item.status).includes("delivered") &&
    !safeLower(item.status).includes("entregado")
  ).length;

  const exportCsv = () => {
    const csv = buildCsv(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "envios_filtrados.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const resetFilters = () => {
    setDraftFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <h1 style={titleStyle}>Envíos</h1>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={() => setFiltersOpen((prev) => !prev)}>
            {filtersOpen ? "Ocultar filtros" : "Filtros"}
          </Button>
          <Button variant="secondary" onClick={exportCsv} disabled={!filteredRows.length}>
            Descargar CSV
          </Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="deliveries-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Total</div>
          <div style={kpiValueStyle}>{totalRows}</div>
          <div style={kpiMetaStyle}>Todos los envíos cargados</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Visibles</div>
          <div style={kpiValueStyle}>{visibleRows}</div>
          <div style={kpiMetaStyle}>Resultado según filtros</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Entregados</div>
          <div style={kpiValueStyle}>{entregadosRows}</div>
          <div style={kpiMetaStyle}>Según estado ML</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Pendientes</div>
          <div style={kpiValueStyle}>{pendientesRows}</div>
          <div style={kpiMetaStyle}>Operación en curso</div>
        </Card>
      </div>

      {filtersOpen ? (
        <Card>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "14px",
            }}
            className="deliveries-filters-grid"
          >
            <SelectField
              label="Estado"
              value={draftFilters.estado}
              onChange={(value) => setDraftFilters((prev) => ({ ...prev, estado: value }))}
              options={estadoOptions}
            />

            <SelectField
              label="Origen"
              value={draftFilters.origen}
              onChange={(value) => setDraftFilters((prev) => ({ ...prev, origen: value }))}
              options={origenOptions}
            />

            <SelectField
              label="Tipo envío"
              value={draftFilters.tipoEnvio}
              onChange={(value) => setDraftFilters((prev) => ({ ...prev, tipoEnvio: value }))}
              options={tipoEnvioOptions}
            />

            <SelectField
              label="Turbo"
              value={draftFilters.turbo}
              onChange={(value) => setDraftFilters((prev) => ({ ...prev, turbo: value }))}
              options={["Todos", "Sí", "No"]}
            />

            <Input
              label="Tracking"
              placeholder="Buscar tracking"
              value={draftFilters.tracking}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, tracking: e.target.value }))}
            />

            <Input
              label="Tienda"
              placeholder="Nombre de tienda"
              value={draftFilters.storeName}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, storeName: e.target.value }))}
            />

            <Input
              label="Comprador"
              placeholder="Nombre del comprador"
              value={draftFilters.destinatario}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, destinatario: e.target.value }))
              }
            />

            <Input
              label="Chofer"
              placeholder="Chofer asignado"
              value={draftFilters.chofer}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, chofer: e.target.value }))}
            />

            <Input
              label="Localidad"
              placeholder="Localidad"
              value={draftFilters.localidad}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, localidad: e.target.value }))}
            />

            <Input
              label="Código postal"
              placeholder="CP"
              value={draftFilters.codigoPostal}
              onChange={(e) =>
                setDraftFilters((prev) => ({ ...prev, codigoPostal: e.target.value }))
              }
            />

            <div style={{ gridColumn: "span 2" }}>
              <Input
                label="Búsqueda global"
                placeholder="Buscar en varios campos"
                value={draftFilters.search}
                onChange={(e) => setDraftFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "18px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button variant="ghost" onClick={resetFilters}>
              Restablecer
            </Button>
            <Button variant="primary" onClick={applyFilters}>
              Aplicar filtros
            </Button>
          </div>
        </Card>
      ) : null}

      <Card style={{ padding: "10px 0 0 0" }}>
        {loading ? (
          <div style={emptyStateStyle}>Cargando envíos...</div>
        ) : error ? (
          <div style={{ ...emptyStateStyle, color: "var(--danger)" }}>{error}</div>
        ) : !filteredRows.length ? (
          <div style={emptyStateStyle}>No hay envíos para los filtros seleccionados.</div>
        ) : (
          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: "70px" }}>QR</th>
                  <th style={thStyle}>Tracking</th>
                  <th style={thStyle}>Tienda</th>
                  <th style={thStyle}>Comprador</th>
                  <th style={thStyle}>Dirección</th>
                  <th style={thStyle}>CP</th>
                  <th style={thStyle}>Localidad</th>
                  <th style={thStyle}>Zona de entrega</th>
                  <th style={thStyle}>Chofer asignado</th>
                  <th style={thStyle}>Estado</th>
                </tr>
              </thead>

              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedRow(row)}
                    style={{
                      cursor: "pointer",
                      transition: "background var(--transition-base)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.025)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "40px",
                          height: "40px",
                          borderRadius: "12px",
                          border: "1px solid var(--border)",
                          background: "rgba(255,255,255,0.04)",
                          fontSize: "18px",
                        }}
                        title="QR pendiente"
                      >
                        ⌘
                      </div>
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{row.tracking || "—"}</div>
                      <div style={mutedTextStyle}>#{row.id}</div>
                    </td>

                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{row.store_name || "—"}</div>
                      <div style={mutedTextStyle}>{row.tipo_envio || "—"}</div>
                    </td>

                    <td style={tdStyle}>{row.nombre_destinatario || "—"}</td>

                    <td style={tdStyle}>
                      <div>{row.direccion || "—"}</div>
                    </td>

                    <td style={tdStyle}>{row.codigo_postal || "—"}</td>

                    <td style={tdStyle}>{row.localidad || "—"}</td>

                    <td style={tdStyle}>
                      <span style={badgeStyle("neutral")}>{row.zona_entrega || "—"}</span>
                    </td>

                    <td style={tdStyle}>
                      {row.chofer && row.chofer !== "—" ? (
                        <span style={badgeStyle("primary")}>{row.chofer}</span>
                      ) : (
                        <span style={badgeStyle("neutral")}>Sin asignar</span>
                      )}
                    </td>

                    <td style={tdStyle}>
                      <span style={badgeStyle(getStatusTone(row.status))}>
                        {row.status || "Sin estado"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <DetailsModal row={selectedRow} onClose={() => setSelectedRow(null)} />

      <style>
        {`
          .deliveries-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .deliveries-filters-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .deliveries-kpi-grid,
            .deliveries-filters-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .deliveries-kpi-grid,
            .deliveries-filters-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}